import {
  IonContent,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { GET_SEASON_COMPETITION_TEAM_ROSTER } from '../../../graphql/queries/getSeasonCompetitionTeamRoster';
import type {
  GetSeasonCompetitionTeamRosterQuery,
  GetSeasonCompetitionTeamRosterQueryVariables,
  Player,
} from '../../../types/graphql-generated';
import { PlayerRoleEnum } from '../../../types/graphql-generated';
import { PlayerCard } from '../../fantasy-leagues-tabs/components/PlayerCard';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { ContentSectionHeader } from '../../../components/ui/ContentSectionHeader';
import {
  DEFAULT_PLAYER_ROLE_BAR_COLOR,
  PLAYER_ROLE_BAR_COLORS,
} from '../../../constants/playerRoleColors';

const ROLE_LABELS: Record<PlayerRoleEnum, string> = {
  [PlayerRoleEnum.Portero]: 'Porteros',
  [PlayerRoleEnum.Defensa]: 'Defensas',
  [PlayerRoleEnum.Centrocampista]: 'Centrocampistas',
  [PlayerRoleEnum.Delantero]: 'Delanteros',
};

const ROLE_ORDER: PlayerRoleEnum[] = [
  PlayerRoleEnum.Portero,
  PlayerRoleEnum.Defensa,
  PlayerRoleEnum.Centrocampista,
  PlayerRoleEnum.Delantero,
];

const SeasonRosterPage: React.FC = () => {
  const seasonCompetitionTeamId = useParams<'seasonCompetitionTeamId'>().seasonCompetitionTeamId!;

  const { data, loading, error } = useQuery<
    GetSeasonCompetitionTeamRosterQuery,
    GetSeasonCompetitionTeamRosterQueryVariables
  >(GET_SEASON_COMPETITION_TEAM_ROSTER, {
    variables: { seasonCompetitionTeamId },
  });

  const roster = data?.getSeasonCompetitionTeamRoster;

  type RosterPlayer = NonNullable<typeof roster>['players'][number];
  const playersByRole: Record<PlayerRoleEnum, RosterPlayer[]> = {
    [PlayerRoleEnum.Portero]: [],
    [PlayerRoleEnum.Defensa]: [],
    [PlayerRoleEnum.Centrocampista]: [],
    [PlayerRoleEnum.Delantero]: [],
  };

  const sortedPlayers = [...(roster?.players ?? [])].sort(
    (a, b) => (b.marketValue ?? 0) - (a.marketValue ?? 0)
  );
  const shouldSplitFormerPlayers = roster?.seasonCompetition.active ?? false;
  const currentPlayers = shouldSplitFormerPlayers
    ? sortedPlayers.filter((player) => player.team?.id === roster?.team.id)
    : sortedPlayers;
  const otherPlayers = shouldSplitFormerPlayers
    ? sortedPlayers.filter((player) => player.team?.id !== roster?.team.id)
    : [];
  const rolelessPlayers = currentPlayers.filter((player) => !player.role);

  currentPlayers.forEach((player: RosterPlayer) => {
    if (player.role) {
      playersByRole[player.role as PlayerRoleEnum].push(player);
    }
  });

  const hasPlayers = (roster?.players ?? []).length > 0;

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/competition" />
          </IonButtons>
          <IonTitle className="ml-4">{roster?.team.nameShow || 'Plantilla'}</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <IonSpinner />
          </div>
        )}

        {error && (
          <div style={{ padding: '16px', textAlign: 'center', color: 'red' }}>
            Error al cargar la plantilla: {error.message}
          </div>
        )}

        {roster && (
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h1 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 'bold' }}>
                {roster.team.nameShow}
              </h1>
              <IonText color="medium">
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {roster.seasonCompetition.season.displayName} · {roster.seasonCompetition.competition.displayName}
                </p>
              </IonText>
            </div>

            {!hasPlayers && (
              <IonText color="medium">
                <p>No hay jugadores en esta plantilla</p>
              </IonText>
            )}

            {ROLE_ORDER.map((role) => {
              const players = playersByRole[role];
              if (players.length === 0) return null;

              return (
                <div key={role} style={{ marginBottom: '24px' }}>
                  <ContentSectionHeader
                    label={ROLE_LABELS[role]}
                    count={players.length}
                    barColor={PLAYER_ROLE_BAR_COLORS[role]}
                    className="mb-2"
                  />
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {players.map((player: typeof players[number]) => (
                      <PlayerCard key={player.id} player={player as Player} />
                    ))}
                  </div>
                </div>
              );
            })}

            {rolelessPlayers.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <ContentSectionHeader
                  label="Otros jugadores"
                  count={rolelessPlayers.length}
                  barColor={DEFAULT_PLAYER_ROLE_BAR_COLOR}
                  className="mb-2"
                />
                <div style={{ display: 'grid', gap: '8px' }}>
                  {rolelessPlayers.map((player) => (
                    <PlayerCard key={player.id} player={player as Player} />
                  ))}
                </div>
              </div>
            )}

            {otherPlayers.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <ContentSectionHeader
                  label="Otros jugadores de esta plantilla durante la temporada"
                  count={otherPlayers.length}
                  barColor={DEFAULT_PLAYER_ROLE_BAR_COLOR}
                  className="mb-2"
                />
                <div style={{ display: 'grid', gap: '8px' }}>
                  {otherPlayers.map((player) => (
                    <PlayerCard key={player.id} player={player as Player} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default SeasonRosterPage;
